import { createSelector } from '@/redux/lib/createSelector.js';
import { param } from './uiSelectors.js';

export const docsLoadingByAppId = createSelector('docsLoadingByAppId', [(state) => state?.ai?.docsLoading, (state) => param(state, 'appId')], (docsLoading, appId) => docsLoading?.[appId]);

const VERSION_ORDER = ['summary', 'full', 'raw'];
const RESEARCH_DOC_TYPES = ['authentication', 'filtering', 'pagination', 'sorting'];
const APP_DRAWER_SECTION_ORDER = ['app', 'filtering', 'pagination', 'sorting'];

const mapRouteSectionToDocType = (section) => {
  if (!section) {
    return 'app';
  }
  if (section === 'actions') {
    return 'action';
  }
  if (section === 'auth') {
    return 'authentication';
  }
  return section;
};

const normalizeTypePrimaryKey = (type, objectId) => {
  if (type === 'app') {
    return null;
  }

  if (RESEARCH_DOC_TYPES.includes(type) && (objectId === null || objectId === undefined || objectId === '' || objectId === 'default')) {
    return null;
  }

  return objectId;
};

const filterRowsByTypeAndObject = ({ appDoc, appId, type, objectId }) => {
  if (!appId || !type) {
    return [];
  }

  const typePrimaryKey = normalizeTypePrimaryKey(type, objectId);
  return appDoc?.filter((n) => n?.appId === appId && n?.type === type && (typePrimaryKey == null ? n?.typePrimaryKey == null : n?.typePrimaryKey === typePrimaryKey)) ?? [];
};

const getOrderedVersions = (rows) => {
  const versions = [...new Set(rows.map((r) => r.version).filter(Boolean))];
  return VERSION_ORDER.filter((v) => versions.includes(v));
};

const pickPrimaryDoc = (rows) => {
  if (!rows?.length) return null;
  const summary = rows.find((r) => r.version === 'summary');
  return summary ?? rows[0];
};

export const appActionDoc = createSelector(
  'appActionDoc',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (state) => param(state, 'arg1')], //
  (appDoc, appId, actionId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type: 'action', objectId: actionId });
    return pickPrimaryDoc(rows);
  }
);

export const appDoc = createSelector(
  'appDoc',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId')], //
  (appDoc, appId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type: 'app', objectId: null });
    return pickPrimaryDoc(rows);
  }
);

export const appDocByTypeAndObject = createSelector(
  'appDocByTypeAndObject',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (_, type) => type, (_, __, objectId) => objectId], //
  (appDoc, appId, type, objectId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return pickPrimaryDoc(rows);
  }
);

export const appDocAvailableVersionsByTypeAndObject = createSelector(
  'appDocAvailableVersionsByTypeAndObject',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (_, type) => type, (_, __, objectId) => objectId], //
  (appDoc, appId, type, objectId) => {
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return getOrderedVersions(rows);
  }
);

export const appDocAvailableAppSections = createSelector(
  'appDocAvailableAppSections',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId')], //
  (appDoc, appId) =>
    APP_DRAWER_SECTION_ORDER.filter((type) => {
      const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId: null });
      return getOrderedVersions(rows).length > 0;
    })
);

export const appDocAvailableVersions = createSelector(
  'appDocAvailableVersions',
  [(state) => state?.ai?.appDoc, (state) => param(state, 'appId'), (state) => param(state, 'section'), (state) => param(state, 'arg1')], //
  (appDoc, appId, section, arg1) => {
    const type = mapRouteSectionToDocType(section);
    const objectId = type === 'app' ? null : arg1;
    const rows = filterRowsByTypeAndObject({ appDoc, appId, type, objectId });
    return getOrderedVersions(rows);
  }
);
